import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, formatTarih } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import Ipucu from '../../ui/Ipucu';
import { TeslimRozeti } from '../../ui/HatirlatCekmece';

/**
 * BORÇ HATIRLATMA › YANITLAR — müşterilerin WhatsApp'tan yazdıkları.
 * Sol: yanıt yazan cariler (okunmamış rozetiyle) ve hiçbir cariye bağlanamayan numaralar.
 * Sağ: seçilen carinin konuşması (giden hatırlatmalar + gelenler) ve — müşteri son 24 saatte
 * yazdıysa — serbest metinle yanıt kutusu. Kurallar sunucuda (`routes/whatsapp.ts`).
 */
type Konusma = { customerId: number; cari: string; code: string | null; okunmamis: number; son: string; sonMetin: string; dur: boolean };
type Eslesmeyen = { numara: string; adet: number; son: string; sonMetin: string; dur: boolean };
type Liste = { konusmalar: Konusma[]; eslesmeyen: Eslesmeyen[]; okunmamis: number; hazir: boolean };
type Ileti = { kimlik: string; yon: 'GELEN' | 'GIDEN'; tur: string; metin: string; at: string; durum: string; hata: string | null; teslimAt: string | null; okunduAt: string | null; kim: string | null; kaynak: string | null; dur: boolean };
type KonusmaVeri = {
  cari: { id: number; name: string; code: string | null; phone: string | null; kapali: boolean; kapaliNot: string | null };
  numara: string | null; pencere: { acik: boolean; sonGelen: string | null; bitis: string | null }; hazir: boolean; iletiler: Ileti[];
};

const hataMetni = (e: unknown, yedek: string) => (e instanceof Error ? e.message : yedek);
const numaraYaz = (n: string) => (n.startsWith('90') && n.length === 12 ? `0${n.slice(2, 5)} ${n.slice(5, 8)} ${n.slice(8, 10)} ${n.slice(10)}` : `+${n}`);
const KAYNAK_AD: Record<string, string> = { YANIT: 'yanıt', OTOMATIK: 'otomatik onay yanıtı', ELLE: 'hatırlatma · elle', GENEL: 'ileti', DENEME: 'deneme' };

export default function WhatsappYanitlar({ surum, yenile, sayiVer }: { surum: number; yenile: () => void; sayiVer?: (n: number) => void }) {
  const { can } = useAuth();
  const yazar = can('reminder.send');
  const [liste, setListe] = useState<Liste | null>(null);
  const [secili, setSecili] = useState<number | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [baglanan, setBaglanan] = useState<string | null>(null);

  const yukle = useCallback(() => {
    api<Liste>('/api/whatsapp/yanitlar').then((v) => { setListe(v); sayiVer?.(v.okunmamis); setHata(null); }).catch((e) => setHata(hataMetni(e, 'Yanıtlar alınamadı')));
  }, [sayiVer]);
  useEffect(() => { yukle(); }, [yukle, surum]);
  // Yeni yanıt sayfayı yenilemeden görünsün: ekran açıkken dakikada bir bakılır.
  useEffect(() => { const t = setInterval(yukle, 60_000); return () => clearInterval(t); }, [yukle]);

  if (!liste) return hata ? <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div> : <div className="ccd-inline-note">Yükleniyor…</div>;
  const bos = liste.konusmalar.length === 0 && liste.eslesmeyen.length === 0;

  return (
    <>
      <div className="ccd-meta">
        Müşterilerin WhatsApp'tan yazdıkları <Ipucu id="whatsapp-yanit" />. Müşteri yazdıktan sonraki 24 saat içinde serbest metinle yanıt verebilirsiniz; süre geçince yalnız onaylı şablon gider.
        {!liste.hazir && <> <b>WhatsApp bağlı değil (deneme kipi):</b> yanıtlar ancak kurulum tamamlanınca düşer — <Link className="ccd-link" to="/ayarlar/whatsapp">Ayarlar › WhatsApp</Link>.</>}
      </div>
      {bilgi && <div className="ccd-banner" role="status"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span style={{ flex: 1 }}>{bilgi}</span><button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setBilgi(null)} aria-label="Kapat">✕</button></div>}
      {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}
      {bos ? <div className="ccd-empty">Henüz WhatsApp'tan gelen yanıt yok.</div> : (
        <div className="ccd-wa-yanit" data-tur="whatsapp-yanitlar">
          <div className="ccd-wa-liste">
            {liste.konusmalar.map((k) => (
              <button key={k.customerId} type="button" className={`ccd-wa-satir${secili === k.customerId ? ' ccd-wa-secili' : ''}`} aria-pressed={secili === k.customerId} onClick={() => setSecili(k.customerId)}>
                <span className="ccd-wa-satir-ust">
                  <b>{k.cari}</b>
                  {k.okunmamis > 0 && <span className="ccd-badge ccd-badge-danger" title="Okunmamış ileti">{k.okunmamis}</span>}
                  {k.dur && <span className="ccd-badge ccd-badge-warning" title="Müşteri hatırlatmayı durdurdu">DUR yazdı</span>}
                </span>
                <span className="ccd-wa-satir-metin">{k.sonMetin}</span>
                <span className="ccd-meta">{formatTarih(k.son)}</span>
              </button>
            ))}
            {liste.eslesmeyen.length > 0 && (
              <>
                <div className="ccd-fieldset-title ccd-wa-ayrac">Eşleşmeyen numaralar <Ipucu id="whatsapp-eslesmeyen" /></div>
                {liste.eslesmeyen.map((e) => (
                  <div key={e.numara} className="ccd-wa-satir ccd-wa-duz">
                    <span className="ccd-wa-satir-ust">
                      <b className="ccd-mono">{numaraYaz(e.numara)}</b>
                      <span className="ccd-badge">{e.adet} ileti</span>
                      {e.dur && <span className="ccd-badge ccd-badge-danger" title="Bu numara DUR yazdı ama cari bulunamadığı için kimse kapatılamadı">DUR yazdı — kapatılamadı</span>}
                    </span>
                    <span className="ccd-wa-satir-metin">{e.sonMetin}</span>
                    <span className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <span className="ccd-meta">{formatTarih(e.son)}</span>
                      {yazar && <button type="button" className="ccd-borc-bag" onClick={() => setBaglanan(baglanan === e.numara ? null : e.numara)}>{baglanan === e.numara ? 'vazgeç' : 'cariye bağla'}</button>}
                    </span>
                    {baglanan === e.numara && (
                      <CariBagla numara={e.numara} durVar={e.dur} bitti={(m) => { setBaglanan(null); setBilgi(m); yukle(); yenile(); }} />
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
          <div className="ccd-wa-konusma">
            {secili == null ? <div className="ccd-empty">Konuşmayı görmek için soldan bir cari seçin.</div>
              : <KonusmaKutusu key={secili} cariId={secili} degisti={() => { yukle(); yenile(); }} />}
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Konuşma ────────────────────────────────────────────────────────────── */
function KonusmaKutusu({ cariId, degisti }: { cariId: number; degisti: () => void }) {
  const { can } = useAuth();
  const yazar = can('reminder.send');
  const [v, setV] = useState<KonusmaVeri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [metin, setMetin] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const son = useRef<HTMLDivElement>(null);
  const gorulduYazildi = useRef(false);

  const yukle = useCallback(() => {
    api<KonusmaVeri>(`/api/whatsapp/konusma/${cariId}`).then((d) => {
      setV(d);
      // Konuşma açıldı: gelenler "görüldü" olur (okunmamış rozeti düşer). Bir kez.
      if (!gorulduYazildi.current && d.iletiler.some((i) => i.yon === 'GELEN' && i.durum === 'YENI')) {
        gorulduYazildi.current = true;
        void api(`/api/whatsapp/konusma/${cariId}/goruldu`, { method: 'POST', body: '{}' }).then(degisti).catch(() => undefined);
      }
    }).catch((e) => setHata(hataMetni(e, 'Konuşma alınamadı')));
  }, [cariId, degisti]);
  useEffect(() => { yukle(); }, [yukle]);
  useEffect(() => { son.current?.scrollIntoView({ block: 'nearest' }); }, [v?.iletiler.length]);

  async function gonder() {
    if (!metin.trim()) return;
    setMesgul(true); setHata(null);
    try {
      const r = await api<{ durum: string }>(`/api/whatsapp/konusma/${cariId}/yanit`, { method: 'POST', body: JSON.stringify({ metin }) });
      if (r.durum === 'DENEME') setHata('WhatsApp bağlı değil (deneme kipi): yanıt kayda yazıldı ama GÖNDERİLMEDİ.');
      setMetin(''); yukle(); degisti();
    } catch (e) {
      setHata(e instanceof ApiError && e.status === 409 ? 'Pencere kapandı: müşteri son 24 saatte yazmadı. Serbest metin gönderilemez — cariye onaylı şablonla hatırlatma gönderin.' : hataMetni(e, 'Yanıt gönderilemedi'));
      yukle();
    } finally { setMesgul(false); }
  }

  if (!v) return hata ? <div className="ccd-field-error">{hata}</div> : <div className="ccd-inline-note">Yükleniyor…</div>;
  return (
    <div className="ccd-stack-sm">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <b>{can('quote.view') ? <Link to={`/crm/${v.cari.id}`} title="Cari kartı">{v.cari.name}</Link> : v.cari.name}</b>
          <div className="ccd-meta">{[v.cari.code, v.numara ? numaraYaz(v.numara) : v.cari.phone].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {v.cari.kapali && <span className="ccd-badge ccd-badge-warning" title={v.cari.kapaliNot ?? undefined}>hatırlatma kapalı</span>}
          <span className={`ccd-badge ${v.pencere.acik ? 'ccd-badge-success' : ''}`} data-fihrist="whatsapp-pencere">
            {v.pencere.acik ? `yanıt penceresi açık · ${formatTarih(v.pencere.bitis!)} tarihine kadar` : 'yanıt penceresi kapalı'}
          </span>
        </div>
      </div>
      <div className="ccd-wa-akis" aria-label="Konuşma">
        {v.iletiler.length === 0 && <div className="ccd-meta">Bu cariyle görebileceğiniz WhatsApp iletisi yok.</div>}
        {v.iletiler.map((i) => (
          <div key={i.kimlik} className={`ccd-wa-ileti ${i.yon === 'GELEN' ? 'ccd-wa-gelen' : 'ccd-wa-giden'}`}>
            <div className="ccd-wa-ileti-metin">{i.metin}</div>
            <div className="ccd-wa-ileti-alt">
              {i.yon === 'GIDEN' && <span>{i.tur === 'hatirlatma' ? `hatırlatma · ${i.kaynak === 'ELLE' ? 'elle' : 'otomatik'}` : KAYNAK_AD[i.kaynak ?? ''] ?? 'ileti'}{i.kim && i.kim !== 'otomatik' ? ` · ${i.kim}` : ''}</span>}
              {i.dur && <span className="ccd-badge ccd-badge-warning">durdurma isteği</span>}
              <span>{formatTarih(i.at)}</span>
              {i.yon === 'GIDEN' && <TeslimRozeti durum={i.durum} teslimAt={i.teslimAt} okunduAt={i.okunduAt} hata={i.hata} />}
            </div>
          </div>
        ))}
        <div ref={son} />
      </div>
      {hata && <div className="ccd-field-error">{hata}</div>}
      {yazar && (v.pencere.acik ? (
        <div className="ccd-wa-yaz">
          <textarea className="ccd-textarea" rows={2} placeholder="Yanıtınızı yazın…" aria-label="WhatsApp yanıtı" value={metin} maxLength={4000} disabled={mesgul} onChange={(e) => setMetin(e.target.value)} />
          <button type="button" className="ccd-btn ccd-btn-primary" disabled={mesgul || !metin.trim()} onClick={() => void gonder()}>{mesgul ? 'Gönderiliyor…' : 'Gönder'}</button>
        </div>
      ) : (
        <div className="ccd-wa-kapali">
          <b>Pencere kapandı — şablon gönderin.</b> Müşteri son 24 saatte yazmadığı için serbest metin gönderilemez.
          Onaylı şablonla hatırlatma göndermek için Borçlular sekmesinde carinin "Hatırlat" düğmesini kullanın.
        </div>
      ))}
    </div>
  );
}

/* ─── Eşleşmeyen numarayı cariye bağla ───────────────────────────────────── */
function CariBagla({ numara, durVar, bitti }: { numara: string; durVar: boolean; bitti: (mesaj: string) => void }) {
  const [q, setQ] = useState('');
  const [sonuc, setSonuc] = useState<{ id: number; name: string; code: string | null; phone: string | null }[]>([]);
  const [hata, setHata] = useState<string | null>(null);
  useEffect(() => {
    if (q.trim().length < 2) { setSonuc([]); return undefined; }
    let iptal = false;
    const t = setTimeout(() => {
      api<{ satirlar: typeof sonuc }>(`/api/whatsapp/cari-ara?q=${encodeURIComponent(q.trim())}`).then((v) => { if (!iptal) setSonuc(v.satirlar); }).catch(() => undefined);
    }, 250);
    return () => { iptal = true; clearTimeout(t); };
  }, [q]);

  async function bagla(c: { id: number; name: string }) {
    if (!window.confirm(`${numaraYaz(numara)} numarası "${c.name}" carisine bağlansın mı?\n\nBu numaradan gelen iletiler (eskileri ve yenileri) bu carinin konuşmasında görünecek.`)) return;
    try {
      const r = await api<{ tasinan: number; durVar: boolean }>('/api/whatsapp/eslesmeyen/bagla', { method: 'POST', body: JSON.stringify({ numara, customerId: c.id }) });
      bitti(`${r.tasinan} ileti "${c.name}" carisine bağlandı.${r.durVar ? ' DİKKAT: bu numara "DUR" yazmıştı; cari kendiliğinden KAPATILMADI — cari kartından "Bu cariye hatırlatma gönderme" anahtarını açın.' : ''}`);
    } catch (e) { setHata(hataMetni(e, 'Bağlanamadı')); }
  }

  return (
    <div className="ccd-wa-bagla">
      <input className="ccd-input" autoFocus placeholder="Cari adı, kodu ya da telefonu…" aria-label="Bağlanacak cariyi ara" value={q} onChange={(e) => setQ(e.target.value)} />
      {durVar && <div className="ccd-meta">Bu numara "DUR" yazdı; bağladıktan sonra cariyi hatırlatmaya kapatmayı unutmayın.</div>}
      {hata && <div className="ccd-field-error">{hata}</div>}
      {sonuc.map((c) => (
        <button key={c.id} type="button" className="ccd-wa-aday" onClick={() => void bagla(c)}>
          <b>{c.name}</b><span className="ccd-meta">{[c.code, c.phone].filter(Boolean).join(' · ')}</span>
        </button>
      ))}
      {q.trim().length >= 2 && sonuc.length === 0 && <div className="ccd-meta">Eşleşen cari yok.</div>}
    </div>
  );
}
